import type { StepStatus } from '@customerio/pluma-components/react';
import type { SqlImportDatabase } from '@/services/db/sql-import-database/entity';
import { ImportType } from '@/services/db/sql-import/entity';
import { isEmptyString } from '@/utils/string';
import type { SqlImportDraft } from './draft';
import { usesPort } from './database-form';

export const WIZARD_STEPS = ['details', 'settings', 'database', 'query', 'relationships', 'review'] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

export const STEP_LABELS = {
  details: 'Sync',
  settings: 'Settings',
  database: 'Database',
  query: 'Query',
  relationships: 'Relationships (optional)',
  review: 'Review',
} as const satisfies Record<WizardStep, string>;

/** The forward button's label per step; the last step creates the sync. */
export const NEXT_LABELS = {
  details: 'Next',
  settings: 'Next',
  database: 'Next',
  query: 'Next',
  relationships: 'Next',
  review: 'Set up sync',
} as const satisfies Record<WizardStep, string>;

/** The backward button's label per step; the first step leaves the wizard. */
export const BACK_LABELS = {
  details: 'Cancel',
  settings: 'Back',
  database: 'Back',
  query: 'Back',
  relationships: 'Back',
  review: 'Back',
} as const satisfies Record<WizardStep, string>;

export type WizardStepState = {
  id: WizardStep;
  label: string;
  status: StepStatus;
  isCurrent: boolean;
  /** Gated: incomplete, with an earlier step still incomplete. Never the current step. */
  isDisabled: boolean;
};

export type WizardProgress = {
  draft: SqlImportDraft;
  /** The database the draft points at, when it is among the workspace's databases. */
  database: SqlImportDatabase | undefined;
  hasSuccessfulQuery: boolean;
  hasSuccessfulRelationshipsQuery: boolean;
  /** Object types exist and the plan allows relationships. */
  withRelationships: boolean;
  nowInSeconds: number;
};

/** The Sync step: a name. */
export function hasBasicDetails(draft: SqlImportDraft): boolean {
  return !isEmptyString(draft.name);
}

/**
 * The Settings step: a positive interval, an id field, a start that is now or in the future, and
 * for an import that fills a segment, the segment to reuse or the name of the one to create.
 */
export function hasRequiredSettings(draft: SqlImportDraft, nowInSeconds: number): boolean {
  const startAt = draft.start_at ?? 0;
  const hasSegment =
    draft.import_type !== ImportType.workspaceAndSegment ||
    !isEmptyString(draft.segment_id) ||
    !isEmptyString(draft.segment_name);
  return (
    (draft.interval ?? 0) > 0 &&
    !isEmptyString(draft.id_field_name) &&
    (startAt === 0 || startAt > nowInSeconds) &&
    hasSegment
  );
}

/** The Database step: a database with the connection fields its engine needs (Snowflake has no port). */
export function hasDatabaseDetails(database: SqlImportDatabase | undefined): boolean {
  if (database == null) {
    return false;
  }
  if (database.type === 'bigquery') {
    return !isEmptyString(database.project_id);
  }
  return (
    !isEmptyString(database.name) &&
    !isEmptyString(database.type) &&
    !isEmptyString(database.username) &&
    !isEmptyString(database.host) &&
    (!usesPort(database.type) || (database.port ?? 0) !== 0)
  );
}

function isComplete(step: WizardStep, progress: WizardProgress): boolean {
  switch (step) {
    case 'details':
      return hasBasicDetails(progress.draft);
    case 'settings':
      return hasRequiredSettings(progress.draft, progress.nowInSeconds);
    case 'database':
      return hasDatabaseDetails(progress.database);
    case 'query':
      return progress.hasSuccessfulQuery;
    case 'relationships':
      return progress.hasSuccessfulRelationshipsQuery;
    case 'review':
      return false;
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}

/** The steps the wizard shows, in order, with completion and gating resolved. */
export function computeWizardSteps(progress: WizardProgress, current: WizardStep): WizardStepState[] {
  const shown = WIZARD_STEPS.filter((step) => step !== 'relationships' || progress.withRelationships);
  const complete = shown.map((step) => isComplete(step, progress));

  return shown.map((step, index) => {
    const isCurrent = step === current;
    const gated = !complete[index] && complete.slice(0, index).some((earlier) => !earlier);
    return {
      id: step,
      label: STEP_LABELS[step],
      status: complete[index] ? 'complete' : 'incomplete',
      isCurrent,
      isDisabled: gated && !isCurrent,
    };
  });
}

/** The step after `current`, or null from the last step (which submits instead). */
export function nextWizardStep(current: WizardStep, withRelationships: boolean): WizardStep | null {
  switch (current) {
    case 'details':
      return 'settings';
    case 'settings':
      return 'database';
    case 'database':
      return 'query';
    case 'query':
      return withRelationships ? 'relationships' : 'review';
    case 'relationships':
      return 'review';
    case 'review':
      return null;
    default: {
      const _exhaustive: never = current;
      return _exhaustive;
    }
  }
}

/** The step before `current`, or null from the first step (which leaves the wizard). */
export function previousWizardStep(current: WizardStep, withRelationships: boolean): WizardStep | null {
  switch (current) {
    case 'details':
      return null;
    case 'settings':
      return 'details';
    case 'database':
      return 'settings';
    case 'query':
      return 'database';
    case 'relationships':
      return 'query';
    case 'review':
      return withRelationships ? 'relationships' : 'query';
    default: {
      const _exhaustive: never = current;
      return _exhaustive;
    }
  }
}

/** The query editors get the wider content column; the forms stay narrow. */
export function wizardContentWidth(step: WizardStep): 'standard' | 'narrow' {
  return step === 'query' || step === 'relationships' ? 'standard' : 'narrow';
}

export type WizardActionState = {
  backLabel: string;
  nextLabel: string;
  /** Set when "Next" submits the step's form rather than moving on directly. */
  formId?: string;
  isNextDisabled: boolean;
  isNextLoading: boolean;
  /** Whether "Next" acts on click; the form-driven step advances on submit instead. */
  withNext: boolean;
  /** The step can be skipped (the optional Relationships step). */
  withSkip?: boolean;
};

/**
 * What the footer does on each step. Details and Settings advance by submitting their forms;
 * Database advances once a complete database is chosen; Query and Relationships once their query
 * previewed successfully (Relationships can also be skipped); Review creates the sync.
 */
type WizardActionInputs = {
  canCreate: boolean;
  isSaving: boolean;
  detailsFormId: string;
  settingsFormId: string;
  /** The Database step has a complete database selected and no form open. */
  canAdvanceDatabase: boolean;
  /** The Query step previewed its query without a blocking error. */
  hasSuccessfulQuery: boolean;
  /** The Relationships step previewed its query without a blocking error. */
  hasSuccessfulRelationshipsQuery: boolean;
};

export function wizardActionState(
  step: WizardStep,
  {
    canCreate,
    isSaving,
    detailsFormId,
    settingsFormId,
    canAdvanceDatabase,
    hasSuccessfulQuery,
    hasSuccessfulRelationshipsQuery,
  }: WizardActionInputs,
): WizardActionState {
  const base = { backLabel: BACK_LABELS[step], nextLabel: NEXT_LABELS[step], isNextLoading: false, withNext: false };
  switch (step) {
    case 'details':
      return { ...base, formId: detailsFormId, isNextDisabled: !canCreate };
    case 'settings':
      return { ...base, formId: settingsFormId, isNextDisabled: !canCreate };
    case 'review':
      return { ...base, isNextDisabled: !canCreate, isNextLoading: isSaving, withNext: true };
    case 'database':
      return { ...base, isNextDisabled: !canCreate || !canAdvanceDatabase, withNext: true };
    case 'query':
      return { ...base, isNextDisabled: !canCreate || !hasSuccessfulQuery, withNext: true };
    case 'relationships':
      return {
        ...base,
        isNextDisabled: !canCreate || !hasSuccessfulRelationshipsQuery,
        withNext: true,
        withSkip: true,
      };
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}
